import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';

/**
 * PokemonEvolutionAnimation
 *
 * Classic Pokémon evolution sequence as a fullscreen overlay:
 *   1. From-sprite enters with a soft glow (0.6s)
 *   2. Cross-fade to a bright white silhouette with floating light
 *      particles, pulsing in scale (2.0s)
 *   3. White flash + light-burst → reveals the to-sprite (0.5s)
 *   4. To-sprite settles with the name announcement (1.4s)
 *   5. onDone fires
 *
 * Props:
 *   - fromSpriteUrl, fromName  — pre-evolution
 *   - toSpriteUrl,   toName    — post-evolution
 *   - onDone: callback when the sequence ends
 *
 * Total runtime ≈ 4.5s. Designed to be skippable by clicking outside.
 */
// Particle layout is generated once at module load — random values must
// not be evaluated during render OR inside effects under the strict
// `react-hooks/purity` + `set-state-in-effect` rules of the new plugin.
const PARTICLES = (() => {
  const out = [];
  for (let i = 0; i < 24; i += 1) {
    const angle = (Math.PI * 2 * i) / 24;
    const dist = 180 + Math.random() * 120;
    out.push({
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      size: 8 + Math.random() * 8,
      duration: 1.6 + Math.random() * 0.6,
      delay: Math.random() * 0.4,
    });
  }
  return out;
})();

export default function PokemonEvolutionAnimation({
  fromSpriteUrl,
  fromName,
  toSpriteUrl,
  toName,
  onDone,
}) {
  // Phases:
  //  0: from-sprite idle glow
  //  1: silhouette pulse with light particles
  //  2: bright flash → reveal
  //  3: to-sprite settled with banner
  //  4: gone
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [];
    const schedule = (ms, fn) => timers.push(setTimeout(fn, ms));
    schedule(600,  () => setPhase(1));    // silhouette pulse
    schedule(2600, () => setPhase(2));    // bright flash
    schedule(3100, () => {                // reveal new sprite
      setPhase(3);
      try {
        confetti({
          particleCount: 80,
          startVelocity: 30,
          spread: 90,
          ticks: 120,
          origin: { y: 0.5 },
          colors: ['#fef08a', '#fde68a', '#fbbf24', '#ffffff'],
        });
      } catch (_e) { /* graceful */ }
    });
    schedule(4500, () => {                // close
      setPhase(4);
      onDone?.();
    });

    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  if (phase >= 4) return null;

  const showFrom = phase === 0 || phase === 1;
  const showSilhouette = phase === 1;
  const showFlash = phase === 2;
  const showTo = phase === 3;

  return (
    <div
      data-testid="evolution-animation-overlay"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.92) 0%, rgba(0,0,0,0.96) 100%)',
        animation: 'evo-fade-in 0.3s ease-out',
      }}
      onClick={(e) => {
        // Allow skipping by clicking the dark backdrop
        if (e.target === e.currentTarget) {
          setPhase(4);
          onDone?.();
        }
      }}
    >
      <style>{`
        @keyframes evo-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes evo-glow {
          0%, 100% { filter: drop-shadow(0 0 12px rgba(254, 240, 138, 0.6)); transform: scale(1); }
          50%      { filter: drop-shadow(0 0 28px rgba(254, 240, 138, 1));   transform: scale(1.04); }
        }
        @keyframes evo-pulse-silhouette {
          0%   { transform: scale(1);    filter: brightness(0) invert(1) drop-shadow(0 0 20px #ffffff); }
          50%  { transform: scale(1.18); filter: brightness(0) invert(1) drop-shadow(0 0 50px #ffffff); }
          100% { transform: scale(1);    filter: brightness(0) invert(1) drop-shadow(0 0 20px #ffffff); }
        }
        @keyframes evo-flash-out { 0% { opacity: 1 } 100% { opacity: 0 } }
        @keyframes evo-reveal {
          0%   { opacity: 0; transform: scale(0.6); filter: brightness(2); }
          60%  { opacity: 1; transform: scale(1.12); filter: brightness(1.4); }
          100% { opacity: 1; transform: scale(1);    filter: brightness(1); }
        }
        @keyframes evo-particle {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
          25%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) scale(0); }
        }
        @keyframes evo-banner-in {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Floating light particles */}
      {showSilhouette && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {PARTICLES.map((p, i) => (
            <span
              key={i}
              className="absolute left-1/2 top-1/2"
              style={{
                width: p.size,
                height: p.size,
                borderRadius: '50%',
                background: 'radial-gradient(circle, #fef9c3, #facc15 60%, transparent)',
                boxShadow: '0 0 16px rgba(250, 204, 21, 0.9)',
                '--tx': `${p.tx}px`,
                '--ty': `${p.ty}px`,
                animation: `evo-particle ${p.duration}s ease-out ${p.delay}s infinite`,
              }}
            />
          ))}
        </div>
      )}

      {/* Bright flash on reveal */}
      {showFlash && (
        <div
          className="absolute inset-0 bg-white pointer-events-none"
          style={{ animation: 'evo-flash-out 0.5s ease-out forwards' }}
        />
      )}

      {/* Sprite stage */}
      <div className="relative flex items-center justify-center" style={{ width: 360, height: 360 }}>
        {/* From sprite (idle glow) */}
        {showFrom && fromSpriteUrl && (
          <img
            src={fromSpriteUrl}
            alt={fromName || 'from'}
            data-testid="evolution-from-sprite"
            className="absolute inset-0 m-auto object-contain"
            style={{
              imageRendering: 'pixelated',
              width: 260,
              height: 260,
              animation: 'evo-glow 1.6s ease-in-out infinite',
            }}
          />
        )}

        {/* White silhouette pulse (uses from-sprite shape) */}
        {showSilhouette && fromSpriteUrl && (
          <img
            src={fromSpriteUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 m-auto object-contain"
            style={{
              imageRendering: 'pixelated',
              width: 260,
              height: 260,
              animation: 'evo-pulse-silhouette 1.0s ease-in-out infinite',
            }}
          />
        )}

        {/* Revealed new sprite */}
        {showTo && toSpriteUrl && (
          <img
            src={toSpriteUrl}
            alt={toName || 'to'}
            data-testid="evolution-to-sprite"
            className="absolute inset-0 m-auto object-contain"
            style={{
              imageRendering: 'pixelated',
              width: 280,
              height: 280,
              animation: 'evo-reveal 0.9s ease-out forwards',
            }}
          />
        )}
      </div>

      {/* Banner */}
      {showTo && (
        <div
          className="mt-8 text-center"
          data-testid="evolution-banner"
          style={{ animation: 'evo-banner-in 0.5s ease-out 0.4s both' }}
        >
          <p className="text-yellow-300 uppercase tracking-widest text-sm font-bold mb-2">
            Parabéns!
          </p>
          <h2 className="text-white text-4xl font-extrabold drop-shadow-[0_4px_24px_rgba(250,204,21,0.6)]">
            {fromName || '?'} evoluiu para {toName || '?'}!
          </h2>
        </div>
      )}
    </div>
  );
}
